import { useCallback, useMemo } from 'react';

import { getProductSelections } from 'features/menu/components/product-detail/product-combo';
import { AddToCartCta } from 'features/menu/components/product-detail/product-cta/add-to-cart-cta';
import { GoNextCta } from 'features/menu/components/product-detail/product-cta/go-next-cta';
import { MissingModifierSelectionCta } from 'features/menu/components/product-detail/product-cta/missing-modifier-selection-cta';
import {
  ActionsWrapper,
  ContentWrapper,
  StickyFooter,
} from 'features/menu/components/product-detail/product-cta/styled';
import { UpdateItemCta } from 'features/menu/components/product-detail/product-cta/update-item-cta';
import { getRequiredModifierInformation } from 'features/menu/components/product-detail/product-cta/utils';
import { ComboSlotType } from 'features/menu/components/product-detail/product-customization/types';
import { getComboSlotType } from 'features/menu/components/product-detail/product-customization/utils';
import {
  isCustomization,
  transformProductWizardStepToEventAttr,
} from 'features/menu/components/product-detail/utils';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { DEFAULT_SLOT_KEY, ProductWizardStep } from 'features/menu/components/product-wizard/types';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import { capitalize } from 'lodash-es';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { isItem, isPicker } from 'state/unified-menu/utils';
import { isWeb } from 'utils/environment';

import { MissingPickerSelectionCta } from './missing-picker-selection-cta';

export const ProductCta = () => {
  const { indexedMenu } = useUnifiedMenuContext();
  const { bottom } = useSafeAreaInsets();
  const { slotsSelections, menuObject, pickerSelections } = useProductWizardContext();
  const { currentFlowStep, nextFlowStep } = useProductWizardFlow();
  const { posServiceMode } = useMenuQueryOptions();

  const modifierInformation = useMemo(() => {
    const shouldComputeRequiredModifiers =
      (currentFlowStep.step === ProductWizardStep.ProductHome && isItem(menuObject)) ||
      isCustomization(currentFlowStep.step);

    if (!shouldComputeRequiredModifiers) {
      return null;
    }

    const slotKey = currentFlowStep.slotKey || DEFAULT_SLOT_KEY;
    const slotSelection = slotsSelections?.[slotKey];
    const selectedItemId = slotSelection?.itemId;

    if (!selectedItemId) {
      return null;
    }

    return getRequiredModifierInformation(
      indexedMenu,
      selectedItemId,
      slotSelection,
      posServiceMode
    );
  }, [
    currentFlowStep.slotKey,
    currentFlowStep.step,
    indexedMenu,
    menuObject,
    slotsSelections,
    posServiceMode,
  ]);

  const renderCta = useCallback(() => {
    const hasMissingModifiers = !!(
      modifierInformation && modifierInformation.modifierRemainingSelections > 0
    );
    if (hasMissingModifiers) {
      return <MissingModifierSelectionCta modifierInformation={modifierInformation} />;
    }

    const hasMissingPickerSelection =
      currentFlowStep.step === ProductWizardStep.ProductHome &&
      isPicker(menuObject) &&
      Object.keys(pickerSelections ?? {}).length === 0 &&
      !nextFlowStep;

    if (hasMissingPickerSelection) {
      return <MissingPickerSelectionCta />;
    }

    if (currentFlowStep.step === ProductWizardStep.ProductCustomization) {
      const slotSelection = slotsSelections?.[currentFlowStep.slotKey!];
      const item = slotSelection?.itemId ? indexedMenu.find(slotSelection.itemId) : null;

      if (!item || !slotSelection?.modifiers) {
        return null;
      }

      const selections = getProductSelections(
        indexedMenu,
        posServiceMode,
        item,
        slotSelection.modifiers
      );

      const eventAttributes = {
        name: item?.name?.locale ?? '',
        Id: item?.id ?? '',
        modifierSelections: selections?.map(selection => selection.name) || [],
      };

      return <UpdateItemCta eventAttributes={eventAttributes} />;
    }
    if (nextFlowStep) {
      const comboSlotType: ComboSlotType = getComboSlotType(indexedMenu, currentFlowStep.slotKey!);

      const selectedItem = slotsSelections?.[currentFlowStep?.slotKey ?? ''];
      const item = selectedItem ? indexedMenu.find(selectedItem.itemId) : null;

      const eventAttributes = {
        currentStep: transformProductWizardStepToEventAttr(
          currentFlowStep.step === ProductWizardStep.ProductHome
            ? ProductWizardStep.ProductHome
            : ProductWizardStep.PickerCustomization
        ),
        comboSlotType: [ComboSlotType.SIDES, ComboSlotType.DRINKS].includes(comboSlotType)
          ? capitalize(comboSlotType.toString() ?? '')
          : '',
        Item: item?.name?.locale ?? '',
      };

      return <GoNextCta eventAttributes={eventAttributes} />;
    }
    return <AddToCartCta />;
  }, [
    currentFlowStep.slotKey,
    currentFlowStep.step,
    indexedMenu,
    menuObject,
    modifierInformation,
    nextFlowStep,
    pickerSelections,
    posServiceMode,
    slotsSelections,
  ]);

  return (
    <StickyFooter
      testID="product-cta-footer-banner"
      style={!isWeb ? { paddingBottom: bottom } : undefined}
    >
      <ContentWrapper>
        <ActionsWrapper>{renderCta()}</ActionsWrapper>
      </ContentWrapper>
    </StickyFooter>
  );
};
